import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import Loader from "../../common/Loader";
import Button from "../../common/Button";
import FormInput from "../../common/FormInput";
import _ from "../../../i18n";
import {
  saveGoPeopleClient,
  saveGoPeopleContact,
  getGoPeopleClientsByUen,
  saveGoPeopleFolderContact,
  saveGoPeopleCommunityRole,
  verifyGoPeopleContactByEmail,
} from "../../../api";
import {
  GENDER_OPTIONS,
  LANGUAGES,
  SELECT_STYLES,
} from "../../../config";
import { validateEmail, validateUen } from "../../../utils";
import styles from "./Contact.module.scss";

const ContactForm = (props) => {
  const auth = useSelector((state) => state.auth);
  const [email, setEmail] = useState("");
  const [lastname, setLastname] = useState("");
  const [firstname, setFirstname] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState(GENDER_OPTIONS[0]);
  const [language, setLanguage] = useState(LANGUAGES[0]);
  const [uen, setUen] = useState("");
  const [uName, setUname] = useState("");
  const [fonction, setFonction] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);

  const [disableFields, setDisableFields] = useState(false);
  const [disableFieldsUen, setDisableFieldsUen] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [fetchedUser, setFetchedUser] = useState(null);
  const [fetchedContact, setFetchedContact] = useState(null);
  const [fetchedFolder, setFetchedFolder] = useState(null);
  const [oldFetchedFolder, setOldFetchedFolder] = useState(null);
  // true quand le UEN saisi = le numéro d'entreprise du cabinet lui-même
  // (is_community). Dans ce cas on rattache la personne à la communauté, pas à
  // un dossier client.
  const [isCommunityUen, setIsCommunityUen] = useState(false);
  const { goPeopleCommunityId, jwtToken, goPeopleOrganizationId } = auth;
  let saveInterval;

  const [errors, setErrors] = useState({
    email: "",
    lastname: "",
    firstname: "",
    phone: "",
    uen: "",
    name: "",
  });

  useEffect(() => {
    if (props.emailValue) {
      setEmail(props.emailValue);
      handleEmailProcess(props.emailValue);
    }
  }, [props.emailValue]);

  useEffect(() => {
    if (props.currentItem) {
      if (props.currentItem.mainEmail) {
        setEmail(props.currentItem.mainEmail);
      }
      setFirstname(
        props.currentItem.firstName ? props.currentItem.firstName : ""
      );
      setLastname(props.currentItem.lastName ? props.currentItem.lastName : "");
      if (props.currentItem.language)
        setLanguage(
          LANGUAGES.filter((e) => e.value === props.currentItem.language)[0]
        );
      setPhone(props.currentItem.mainPhone ? props.currentItem.mainPhone : "");
      if (props.currentItem.gender) {
        const g =
          props.currentItem.gender === "M"
            ? "MALE"
            : props.currentItem.gender === "F"
              ? "FEMALE"
              : props.currentItem.gender;
        const gend = GENDER_OPTIONS.filter((e) => e.value === g);
        if (gend?.length > 0) {
          setGender(gend[0]);
        }
      }
      if (props.currentItem.contactClient?.details) {
        if (props.currentItem.contactClient.details.uen) {
          setUen(props.currentItem.contactClient.details.uen);

          handleUenBlur(props.currentItem.contactClient.details.uen);
        }
        if (props.currentItem.contactClient.details.name)
          setUname(props.currentItem.contactClient.details.name);
        if (props.currentItem.contactClient.details.fonction)
          setFonction(props.currentItem.contactClient.details.fonction);
      }
      setIsValidForm(true);
    }
  }, [props.currentItem]);
  useEffect(() => {
    if (email.length > 0 && firstname.length > 0 && lastname.length > 0) {
      setIsValidForm(
        errors.email || errors.lastname || errors.firstname || errors.phone
          ? false
          : true
      );
    }
  }, [errors]);

  const handleKeyUp = (fieldName, fieldValue) => {
    let value = "";
    if (fieldValue.length === 0) {
      value = _("required_field");
    } else {
      switch (fieldName) {
        case "email":
          if (!validateEmail(fieldValue)) {
            value = _("validate_email");
          }
          break;
        // case "phone":
        //   if (!isValidPhoneNumber(fieldValue)) {
        //     value = _("validate_phone");
        //   }
        //   break;
        case "lastname":
        case "firstname":
          if (fieldValue.length < 2) {
            value = _("required_2_characters");
          }
          break;
      }
    }
    setErrors({ ...errors, [fieldName]: value });
  };

  // Maps the fields returned by the verify endpoint onto the form. The payload
  // may use snake_case (contact record) or camelCase (user record), and phones
  // may arrive as an array or a single string, so we read both shapes.
  const fillFormFromMatch = (d) => {
    setFirstname(d.first_name ?? d.firstname ?? "");
    setLastname(d.last_name ?? d.lastname ?? "");

    // Contact core uses "language"; user core uses "main_language".
    const lang = d.language ?? d.main_language;
    if (lang) {
      const found = LANGUAGES.filter((e) => e.value === lang);
      if (found.length) {
        setLanguage(found[0]);
      }
    }

    // Contact uses "genre"; user uses "sex" (both M/F).
    const rawGender = d.genre ?? d.gender ?? d.sex;
    if (rawGender) {
      const g =
        rawGender === "M" ? "MALE" : rawGender === "F" ? "FEMALE" : rawGender;
      const gend = GENDER_OPTIONS.filter((e) => e.value === g);
      if (gend?.length > 0) {
        setGender(gend[0]);
      }
    }

    const phones = Array.isArray(d.phones)
      ? d.phones
      : d.phone
        ? [d.phone]
        : [];
    if (phones.length) {
      setPhone(phones[0]);
    }
  };

  // The verify endpoint checks the community contacts first, then falls back to
  // a global user. A community contact is serialized with community_id (a core
  // field); a global user is not (it carries selected_community_id instead), so
  // its absence tells the two apart. Extra markers stay as defensive fallbacks.
  const isCommunityContactMatch = (d) => {
    if (d.community_id) return true;
    if (typeof d.is_contact === "boolean") return d.is_contact;
    return Boolean(d.contact_type || d.contact_id);
  };

  const handleEmailProcess = (email) => {
    setIsFetchingUser(true);
    verifyGoPeopleContactByEmail(goPeopleCommunityId, email)
      .then((match) => {
        // 404 -> no existing contact/user: keep the form empty and editable.
        if (!match) {
          setDisableFields(false);
          setFirstname("");
          setLastname("");
          return;
        }

        fillFormFromMatch(match);
        setDisableFields(true);
        setErrors({
          email: "",
          lastname: "",
          firstname: "",
          phone: "",
        });

        // An existing community contact is reused as-is on save (its uuid is the
        // contact id). A global user is turned into a new contact linked via
        // user_id. Both shapes expose their id as "uuid".
        if (isCommunityContactMatch(match)) {
          setFetchedContact({ ...match, uuid: match.uuid ?? match.id });
        } else {
          setFetchedUser({ ...match, uuid: match.uuid ?? match.id });
        }
      })
      .catch((e) => {
        setDisableFields(false);
        handleResetForm(false);
      })
      .finally(() => {
        setIsFetchingUser(false);
      });
  };

  const handleEmailBlur = (e) => {
    if (email.length > 0 && validateEmail(email)) {
      handleEmailProcess(email);
    }
  };

  const handleUenBlur = (value) => {
    if (!value) value = uen;
    if (value.length > 0 && validateUen(value)) {
      setIsFetchingUser(true);
      // On repart d'un état neuf : ce n'est le cabinet que si le backend le dit.
      setIsCommunityUen(false);
      getGoPeopleClientsByUen(jwtToken, goPeopleCommunityId, value)
        .then((response) => {
          const data = response?.data;
          if (data?.is_community) {
            // Le UEN correspond au numéro d'entreprise du cabinet lui-même.
            // Pas de dossier client : au save, on rattachera la personne à la
            // communauté via community-role.
            setIsCommunityUen(true);
            setFetchedFolder(null);
            setUname(data.community?.community_name ?? "");
            setDisableFieldsUen(true);
          } else if (data?.folder) {
            setFetchedFolder(data.folder);
            setUname(data.folder.folder_name);
            setDisableFieldsUen(true);
            if (props.currentItem && !oldFetchedFolder) {
              setOldFetchedFolder(data.folder.uuid);
            }
          }
        })
        .catch(() => {
          // UEN inconnu (aucun dossier, pas le cabinet) : rien a preremplir.
          // Le nom d'entreprise est saisi manuellement et rendu obligatoire au save.
        })
        .finally(() => {
          setIsFetchingUser(false);
        });
    }
  };

  const validate = () => {
    let errors = [];
    handleKeyUp("email", email);
    handleKeyUp("lastname", lastname);
    handleKeyUp("firstname", firstname);
    // handleKeyUp("phone", phone);

    return errors.email || errors.lastname || errors.firstname || errors.phone
      ? true
      : false;
  };

  const save = () => {
    handleSave();
  };

  const afterSave = () => {
    setIsSaving(false);
    props.afterSave();
    toast.success(
      props.currentItem ? _("successfully_updated") : _("successfully_added")
    );
    handleResetForm();
    if (props.setSideBar) {
      props.setSideBar("DEFAULT");
    }
  };

  const saveFolderAndRole = (uuid) => {
    let folderData = {
      uen: uen.replace(/\.|\s|BE|be/g, ""),
      folder_name: uName,
      languages: language ? [language.value] : [],
    };
    saveGoPeopleClient(
      jwtToken,
      goPeopleCommunityId,
      goPeopleOrganizationId,
      folderData
    )
      .then((folderResponse) => {
        saveGoPeopleFolderContact(goPeopleCommunityId, folderResponse.uuid, [
          {
            contact_id: uuid,
            role_type: "member",
          },
        ]).then((folderContactResponse) => {
          afterSave();
          clearInterval(saveInterval);
          setIsSaving(false);
        });
      })
      .catch((e) => {
        clearInterval(saveInterval);
        setIsSaving(false);
        if (e.status === 409) {
          toast.warning(_("contact_already_exist"));
        } else {
          toast.error(_("error"));
        }
      });
  };

  // Cas is_community : rattache la personne au cabinet (communauté) au lieu
  // d'un dossier client. Le contact doit déjà exister (créé/réutilisé avant).
const saveCommunityRole = (contactId) => {
    saveGoPeopleCommunityRole(goPeopleCommunityId, contactId, "member")
      .then(() => {
        afterSave();
        clearInterval(saveInterval);
        setIsSaving(false);
      })
      .catch((e) => {
        clearInterval(saveInterval);
        setIsSaving(false);
        if (e.status === 409) {
          toast.warning(_("contact_already_exist"));
        } else {
          toast.error(_("error"));
        }
      });
  };

  const handleSave = async () => {
    setIsSaving(true);
    let error = validate();
    if (error) {
      return null;
    }
    if (!isFetchingUser && !isSaving) {
      let contactData = {
        emails: [email],
        first_name: firstname,
        last_name: lastname,
        community_id: goPeopleCommunityId,
        phones: phone ? [phone] : [],
        language: language.value,
        genre: gender.value,
        user_id: fetchedUser ? fetchedUser.uuid : null,
      };

      // Un dossier n'est créé que si un UEN est saisi, qu'aucun dossier existant
      // n'a été trouvé, et que ce n'est pas le cabinet lui-même. Dans ce cas le
      // backend exige un nom de dossier : on l'impose ici au lieu de subir un 400.
      const willCreateFolder = uen && !fetchedFolder && !isCommunityUen;
      if (willCreateFolder && !uName.trim()) {
        setErrors({ ...errors, name: _("required_field") });
        setIsSaving(false);
        return;
      }

      if (fetchedContact) {
        if (isCommunityUen) {
          saveCommunityRole(fetchedContact.uuid);
        } else if (fetchedFolder) {
          saveGoPeopleFolderContact(goPeopleCommunityId, fetchedFolder.uuid, [
            {
              contact_id: fetchedContact.uuid,
              role_type: "member",
            },
          ])
            .then((folderContactResponse) => {
              afterSave();
            })
            .catch((e) => {
              clearInterval(saveInterval);
              setIsSaving(false);
              if (e.status === 409) {
                toast.warning(_("contact_already_exist"));
              } else {
                toast.error(_("error"));
              }
            });
        } else if (uen) {
          saveFolderAndRole(fetchedContact.uuid);
        } else {
          afterSave();
        }
      } else {
        saveGoPeopleContact(goPeopleCommunityId, contactData)
          .then((contactResponse) => {
            if (isCommunityUen) {
              saveCommunityRole(contactResponse.uuid);
            } else if (!fetchedFolder) {
              if (uen) {
                saveFolderAndRole(contactResponse.uuid);
              } else {
                afterSave();
              }
            } else {
              saveGoPeopleFolderContact(
                goPeopleCommunityId,
                fetchedFolder.uuid,
                [
                  {
                    contact_id: contactResponse.uuid,
                    role_type: "member",
                  },
                ]
              )
                .then((folderContactResponse) => {
                  afterSave();
                })
                .catch((e) => {
                  clearInterval(saveInterval);
                  setIsSaving(false);
                  if (e.status === 409) {
                    toast.warning(_("contact_already_exist"));
                  } else {
                    toast.error(_("error"));
                  }
                });
            }
          })
          .catch((e) => {
            clearInterval(saveInterval);
            setIsSaving(false);
            if (
              e.response &&
              e.response.data &&
              e.response.data.errors &&
              e.response.data.errors.length > 0
            )
              toast.error(_(e.response.data.errors[0].message));
            else toast.error(_("error"));
          });
      }
    }
  };

  const handleResetForm = (resetEmail = true) => {
    if (resetEmail) {
      setEmail("");
    }
    setFirstname("");
    setLastname("");
    setPhone("");
    setDisableFields(false);
    setGender(GENDER_OPTIONS[0]);
    setLanguage(LANGUAGES[0]);
    setErrors({
      email: "",
      lastname: "",
      firstname: "",
      phone: "",
      uen: "",
      name: "",
    });
    setUen("");
    setUname("");
    setFonction("");
    props.setCurrentItem(null);
    document
      .getElementById("contactForm")
      .scroll({ top: 0, behavior: "smooth" });
    setDisableFieldsUen(false);
    setFetchedUser(null);
    setFetchedFolder(null);
    setOldFetchedFolder(null);
    setIsCommunityUen(false);
  };
  return (
    <div className={props.addFormClass ? styles.form : ""}>
      <div
        id={"contactForm"}
        className={`${styles.form_content} ${
          props.addFormClass && styles.form_content_pad
        } ${props.addFormClass ? styles.form_content_settings : ""} ${
          isFetchingUser && styles.loading
        }`}
      >
        {props.addFormClass && (
          <div className={styles.titles}>
            <h4>Ajout Manuel d'un contact</h4>
            <p>Remplissez les champs pour ajouter un contact</p>
          </div>
        )}
        <FormInput
          name="email"
          required={true}
          label={_("email_address")}
          autocomplete="off"
          error={errors.email}
          value={email}
          onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
          handleBlur={(e) => handleEmailBlur(e)}
          onChange={(e) => setEmail(e.target.value)}
        />

        <div className="grid-x grid-margin-x">
          <div className="cell small-12 medium-6">
            <FormInput
              name="lastname"
              required={true}
              label={_("lastname")}
              autocomplete="off"
              error={errors.lastname}
              value={lastname}
              onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
              onChange={(e) => setLastname(e.target.value)}
              disabled={disableFields}
            />
          </div>
          <div className="cell small-12 medium-6">
            <FormInput
              name="firstname"
              required={true}
              label={_("firstname")}
              autocomplete="off"
              error={errors.firstname}
              value={firstname}
              onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
              onChange={(e) => setFirstname(e.target.value)}
              disabled={disableFields}
            />
          </div>
        </div>
        <div className={styles.phoneGroup}>
          <PhoneInput
            defaultCountry="BE"
            placeholder="Enter phone number"
            value={phone}
            disabled={disableFields}
            onChange={(val) => {
              setPhone(val);
              if (val && !isValidPhoneNumber(val)) {
                setErrors({ ...errors, phone: _("validate_phone") });
              } else {
                setErrors({ ...errors, phone: "" });
              }
            }}
          />
          {/* <span className={styles.errorSpan}>{errors.phone}</span> */}
        </div>

        <div className="grid-x grid-margin-x">
          <div className="cell small-12 medium-6">
            <div className="ttp-form-group">
              <label className="ttp-label">{_("gender_1")}</label>
              <Select
                styles={SELECT_STYLES}
                options={GENDER_OPTIONS}
                isSearchable={false}
                value={gender}
                onChange={(e) => setGender(e)}
                isDisabled={disableFields}
              />
            </div>
          </div>
          <div className="cell small-12 medium-6">
            <div className="ttp-form-group">
              <label className="ttp-label">{_("language")}</label>
              <Select
                styles={SELECT_STYLES}
                options={LANGUAGES}
                isSearchable={false}
                value={language}
                onChange={(e) => setLanguage(e)}
                isDisabled={disableFields}
              />
            </div>
          </div>
        </div>

        <FormInput
          name="uen"
          placeholder="BE XXXX.XXX.XXX"
          label={_("business_number")}
          value={uen}
          autoComplete="none"
          error={errors.uen}
          onChange={(e) => {
            if (e.target.value.length > 0 && !validateUen(e.target.value))
              setErrors({ ...errors, uen: _("validate_uen") });
            else setErrors({ ...errors, uen: _("") });
            setUen(e.target.value);
          }}
          onBlur={(e) => handleUenBlur()}
        ></FormInput>

        <FormInput
          name="name"
          value={uName}
          label={_("company_name")}
          autoComplete="none"
          error={errors.name}
          onChange={(e) => {
            setUname(e.target.value);
            if (errors.name) setErrors({ ...errors, name: "" });
          }}
          disabled={disableFieldsUen}
        ></FormInput>

        <FormInput
          name="fonction"
          label={_("Fonction")}
          value={fonction}
          autoComplete="none"
          onChange={(e) => setFonction(e.target.value)}
        ></FormInput>
      </div>
      <div className={styles.actions}>
        <Button
          variant="default"
          onClick={() => {
            handleResetForm();
            if (props.setSideBar) {
              props.setSideBar("DEFAULT");
            }
          }}
        >
          {_("cancel")}
        </Button>
        {isSaving ? (
          <Button
            variant="primary"
            style={{ paddingTop: "15px", paddingBottom: "15px" }}
          >
            <Loader
              style={{
                height: "10px",
              }}
              color={"#fff"}
            />
          </Button>
        ) : (
          <Button disabled={!isValidForm} onClick={() => save()}>
            {props.currentItem ? _("edit") : _("add")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
